import { findChildren } from "@tiptap/core"
import type { CodeBlockOptions } from "@tiptap/extension-code-block"
import CodeBlock from "@tiptap/extension-code-block"
import type { Node as ProsemirrorNode } from "@tiptap/pm/model"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { detectLanguage, highlight } from "../lib/highlight.js"

interface CodeBlockRefractorOptions extends CodeBlockOptions {
  refractor: any
}

export const CodeBlockRefractor = CodeBlock.extend<CodeBlockRefractorOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      languageClassPrefix: "language-",
      exitOnTripleEnter: true,
      exitOnArrowDown: true,
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      language: {
        default: null,
        parseHTML: element => {
          const { languageClassPrefix } = this.options
          const classNames = [...(element.firstElementChild?.classList || [])]
          const languages = classNames
            .filter(className => className.startsWith(languageClassPrefix))
            .map(className => className.replace(languageClassPrefix, ""))

          const language = languages[0]
          if (!language) return null

          return language
        },
        renderHTML: attributes => {
          return {
            "data-language": attributes.language,
          }
        },
      },
    }
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() || []),
      RefractorPlugin({
        name: this.name,
      }),
    ]
  },
})

function parseNodes(
  nodes: any[],
  className: string[] = [],
): { text: string; classes: string[] }[] {
  return nodes.flatMap(node => {
    const classes = [
      ...className,
      ...(node.properties ? node.properties.className : []),
    ]

    if (node.children) {
      return parseNodes(node.children, classes)
    }

    return {
      text: node.value,
      classes,
    }
  })
}

function getDecorations({ doc, name }: { doc: ProsemirrorNode; name: string }) {
  const decorations: Decoration[] = []

  const children = findChildren(doc, node => node.type.name === name)

  for (const block of children) {
    let from = block.pos + 1

    const code = block.node.textContent
    const language = block.node.attrs.language || detectLanguage({ code })
    const nodes = highlight({ code: block.node.textContent, language })
    if (!nodes) continue

    for (const node of parseNodes(nodes)) {
      const to = from + node.text.length

      if (node.classes.length) {
        const decoration = Decoration.inline(from, to, {
          class: node.classes.join(" "),
        })

        decorations.push(decoration)
      }

      from = to
    }
  }

  return DecorationSet.create(doc, decorations)
}

function RefractorPlugin({ name }: { name: string }) {
  const refractorPlugin: Plugin<any> = new Plugin({
    key: new PluginKey("refractor"),

    state: {
      init: (_, { doc }) =>
        getDecorations({
          doc,
          name,
        }),
      apply: (transaction, decorationSet, oldState, newState) => {
        const oldNodeName = oldState.selection.$head.parent.type.name
        const newNodeName = newState.selection.$head.parent.type.name
        const oldNodes = findChildren(
          oldState.doc,
          node => node.type.name === name,
        )
        const newNodes = findChildren(
          newState.doc,
          node => node.type.name === name,
        )

        if (
          transaction.docChanged &&
          // Apply decorations if:
          // selection includes named node,
          ([oldNodeName, newNodeName].includes(name) ||
            // OR transaction adds/removes named node,
            newNodes.length !== oldNodes.length ||
            // OR transaction has changes that completely encapsulte a node
            // (for example, a transaction that affects the entire document).
            // Such transactions can happen during collab syncing via y-prosemirror, for example.
            transaction.steps.some(step => {
              // @ts-ignore
              return (
                // @ts-ignore
                step.from !== undefined &&
                // @ts-ignore
                step.to !== undefined &&
                oldNodes.some(node => {
                  // @ts-ignore
                  return (
                    // @ts-ignore
                    node.pos >= step.from &&
                    // @ts-ignore
                    node.pos + node.node.nodeSize <= step.to
                  )
                })
              )
            }))
        ) {
          return getDecorations({
            doc: transaction.doc,
            name,
          })
        }

        return decorationSet.map(transaction.mapping, transaction.doc)
      },
    },

    props: {
      decorations(state) {
        return refractorPlugin.getState(state)
      },
    },
  })

  return refractorPlugin
}
